// import { DocumentReaderApi, Scenario, TextFieldType } from "@regulaforensics/document-reader-webclient";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import eventEmitter from "@/utils/event_emmiter_instance.ts";
import CommonClass from "@/utils/common.ts";
import {DocType, SendDocType} from "@/types_and_interfaces/types.ts";
import st from "@/styles/DocReader.module.scss"
import { CircularProgress } from "@mui/material";
import clsx from "clsx";
import { isMobile } from "react-device-detect";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "@/hooks/auth.ts";
import DashboardClass from "@/utils/dashboard_class.ts";
import { kyc_processing_timeout } from "@/utils/constants.ts";
import Images from "@/utils/images.ts";



// const { DOCUMENT_NUMBER, SURNAME_AND_GIVEN_NAMES, DATE_OF_BIRTH } = TextFieldType;

interface Props {
    doc_type: DocType
}

const DocumentReader:React.FC<Props> = ({doc_type}) => {
    const { i18n } = useTranslation()
    const ref = useRef<any>(null)
    const effect_ran = useRef<boolean>(false)
    const [is_loading, set_is_loading] = useState(true)
    const { t } = useTranslation()
    const regula_settings = CommonClass.general_settings.kyc.regula
    const auth = useAuth()
    const location = useLocation()
    const updateProfileInterval = useRef<any>(null)
    const navigate = useNavigate()
    const queryParams = new URLSearchParams(location.search);
    const debug = queryParams.get("debug")
    const [show_start_process_text, set_show_start_process_text ] = useState(false)
    const [show_turn_message, set_show_turn_message] = useState(false)


    useEffect(() => {

        const listener = async (event: any) => {
            const result = CommonClass.doc_reader_event_result(event);

            if(event.detail.action === "NEW_PAGE_AVAILABLE") set_show_turn_message(true)
            if(event.detail.action === "CAMERA_PROCESS_STARTED") set_show_start_process_text(true)

            if (debug === "true") {
                console.info(event.detail)
                console.info("listener", result)
            }

            if (result === "no_camera" || result === "camera_denied") {

                eventEmitter.emit(CommonClass.CustomEvents.switch_doc_reader, result);

            } else if (result === "init") {
                if (ref.current) {
                    ref.current.classList.remove("invisible")
                    ref.current.settings = {mirrorButton: true}
                }
                set_is_loading(false);

            } else if (result === "processing") {
                set_show_start_process_text(false)

                if (debug === "true") {
                    const images = event.detail.data.response.images.fieldList
                    let img_result: any = []

                    if (Array.isArray(images) && images.length) {
                        images.forEach((x) => {
                            let val_list = x.valueList;

                            if (Array.isArray(val_list) && val_list.length) {

                                val_list.forEach((val) => {
                                    img_result.push("data:image/jpeg;base64," + val.value);
                                });

                            }
                        });
                    }

                    if (img_result.length) {
                        img_result.map((x: string) => {
                            const img = new Image();
                            img.src = x;
                            const newWindow = window.open("");
                            newWindow?.document.write(img.outerHTML);
                        })
                    }
                }

                if (ref.current) {
                    ref.current.classList.add("invisible")
                }

                set_is_loading(true)


                try {

                    let attempts = 0;
                    let maxAttempts = 3;
                    let is_finalize_good = true;

                    while (attempts < maxAttempts) {
                        if (debug === "true") console.info("setTimeout finalize package")
                        try {
                            await (window as any).RegulaDocumentSDK.finalizePackage();

                            if (debug === "true") console.info("Package finalized successfully on attempt:", attempts + 1);

                            is_finalize_good = true;
                            break;
                        } catch (e) {
                            attempts++;
                            if (debug === "true") console.error(`Attempt ${attempts} failed:`, e);
                            is_finalize_good = false;

                            eventEmitter.emit(CommonClass.CustomEvents.no_connection_listener_show, true)

                            if (attempts < maxAttempts) {
                                await new Promise(resolve => setTimeout(resolve, import.meta.env.MODE === 'development' ? 10000 : 60000));

                            } else {
                                // alert("Failed to finalize package after 3 attempts. Please try again later.");
                                let arg: SendDocType = "error"
                                eventEmitter.emit(CommonClass.CustomEvents.switch_doc_reader, arg);
                                set_is_loading(false)
                                return;
                            }
                        }
                    }

                    if (is_finalize_good) {
                        const now = Date.now();

                        updateProfileInterval.current = setInterval(async () => {
                            if (debug === "true") console.info("setInterval is_finalize_good")

                            const future = Date.now()
                            const timeDifference = future - now

                            if (timeDifference > kyc_processing_timeout) {
                                eventEmitter.emit(CommonClass.CustomEvents.update_snackbar, "info", t("kyc_will_continue"))
                                navigate(DashboardClass.dashboard_routes.Home)
                                clearInterval(updateProfileInterval.current);
                            }

                            try {
                                const updated = await auth.small_update();

                                const status = updated.kyc.identity_card;

                                let st;

                                switch (status) {
                                    case "ProcessingSuccess":
                                        st = "success";
                                        break;
                                    case "ProcessingFailed":
                                        st = "error";
                                        break;
                                    case "ProcessingFailedAndSentToManager":
                                        st = "fatale_error";
                                        break;
                                    default:
                                        return;
                                }

                                eventEmitter.emit(CommonClass.CustomEvents.switch_doc_reader, st);
                                set_is_loading(false);
                                clearInterval(updateProfileInterval.current);

                            } catch (error) {
                                console.error("Error updating:", error);
                                set_is_loading(false);
                                clearInterval(updateProfileInterval.current);
                            }
                        }, 1500);
                    }


                } catch (e) {
                    console.error(e)
                    let arg: SendDocType = "error"
                    set_is_loading(false)
                    eventEmitter.emit(CommonClass.CustomEvents.switch_doc_reader, arg);
                }

                if (debug === "true") {
                    //TODO : open base 64 images in new tabs
                }



            } else if (result === "timeout") {
                set_show_start_process_text(false)
                const containerListLength = event.detail.data.response.lowLvlResponse.ContainerList.List.length;

                let arg: SendDocType = containerListLength <= 7 ? "timeout_no_doc" : "timeout_doc_blured";

                eventEmitter.emit(CommonClass.CustomEvents.switch_doc_reader, arg);

            } else if (result === "init_error") {
                set_show_start_process_text(false)
                eventEmitter.emit(CommonClass.CustomEvents.switch_doc_reader, result);

            }
        };

        if (!effect_ran.current) {

            const component = document.querySelector('document-reader');

            if (component) {
                // @ts-ignore
                component.settings = {
                    skipButton: false,
                    captureButton: false,
                    // nextPageAnimationEndDelay: 1000,
                    // nextPageAnimationStartDelay: 2000
                };
                component.addEventListener('document-reader', listener);
            }

        }

        return () => {

            if (effect_ran.current) {
                clearInterval(updateProfileInterval.current)
            }

            effect_ran.current = true

        }

    }, []);


    const decodedLicenseKey = atob(regula_settings.license_key);
    const trimmedLicenseKey = decodedLicenseKey.slice(1, -1);

    // https://docs.regulaforensics.com/develop/doc-reader-sdk/web-components/configuration/settings-attributes/
    // https://docs.regulaforensics.com/develop/doc-reader-sdk/web-components/configuration/parameters/

    return (
        <div className={st.doc_reader}>
            {is_loading && (
                <div className={clsx(st.loading_container, isMobile && st.mobile)}>
                    <img src={Images.logos.LogoSimple} alt={"logo"} width={136} height={48} />

                    <CircularProgress color={"success"} />

                    {t("processing")}
                </div>
            )}

            {show_start_process_text ? (
                <div className={clsx(st.message, isMobile && st.message_mobile, show_turn_message && st.anim)}>

                    {doc_type !== "old" ? (
                        <strong>
                            {!show_turn_message ? t("take_picture_of_doc") : t("turn_doc")}
                        </strong>
                    ) : (
                        <strong>
                            {!show_turn_message ? t("take_picture_of_doc1") : t("turn_doc1")}
                        </strong>
                    )}

                </div>
            ) : null}

            {/*@ts-ignore*/}
            <document-reader
                capture-mode={"captureFrame"}
                ref={ref}
                scenario={doc_type === "old" ? regula_settings.scenario_old_identity_card : regula_settings.scenario}
                multipage-processing={true}
                locale={i18n.language}
                license={trimmedLicenseKey}
                copyright={false}
                change-camera={false}
                regula-logo={false}
                change-camera-button={false}
                close-button={false}
                video-capture-motion-control={true}
                class={clsx("document-reader", isMobile && "mobile")}
            />


        </div>
    )
}

export default DocumentReader